import React, { useState } from 'react';
import './common.css'; // Corrected import path
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../contexts/AuthContext';

function Home({ onNavigate }) {
  const { isAuthenticated } = useAuth();
  const [activeIndex, setActiveIndex] = useState(0);

  const prevSlide = () =>
    setActiveIndex(i => (i - 1 + activities.length) % activities.length);
  const nextSlide = () =>
    setActiveIndex(i => (i + 1) % activities.length);

  const handleLevelClick = (level) => {
    if (!isAuthenticated) {
      alert('Please sign in to access levels');
      return;
    }

    onNavigate(`level${level}`);
  };

  const handleActivityClick = (activityName) => {
    if (!isAuthenticated) {
      alert('Please sign in to access activities');
      return;
    }
    
    if (activityName === 'Spelling Assistant') {
      onNavigate('spellingassistant');
    } else if (activityName === 'Drag & Drop Activities') {
      onNavigate('dragdrop');
    } else if (activityName === 'Missing Letters') {
      onNavigate('missingletters');
    } else if (activityName === '24/7 Tutoring') {
      onNavigate('chattutor');
    } else if (activityName === 'Phoneme') {
      onNavigate('phoneme');
    } else if (activityName === 'Sentence Formation') {
      onNavigate('sentenceformation');
    } else if (activityName === 'Math Activities') {
      onNavigate('mathactivities');
    }
  };

  const activities = [
    {
      name: 'Phoneme',
      icon: '🔊',
      description: 'Learn and practice phonemes interactively',
      color: '#E2A8D5',
      patternClass: 'card-pattern-zigzag'
    },
    {
      name: 'Spelling Assistant',
      icon: '✏️',
      description: 'Practice spelling with interactive word games',
      color: '#A8D5E2',
      patternClass: 'card-pattern-dots'
    },
    {
      name: 'Drag & Drop Activities',
      icon: '🎯',
      description: 'Match words, letters, and pictures',
      color: '#F9B5AC',
      patternClass: 'card-pattern-stripes'
    },
    {
      name: 'Missing Letters',
      icon: '📖',
      description: 'Complete the missing syllables in words',
      color: '#B5EAD7',
      patternClass: 'card-pattern-grid'
    },
    {
      name: 'Sentence Formation',
      icon: '🧩',
      description: 'Arrange word tiles to build correct sentences',
      color: '#C7CEEA',
      patternClass: 'card-pattern-stars'
    },
    {
      name: 'Math Activities',
      icon: '🔢',
      description: 'Explore numbers with 12 fun maths games',
      color: '#B5EAD7',
      patternClass: 'card-pattern-grid'
    },
  ];

  return (
    <div className="home-container">
      {/* Header */}
      <Header onNavigate={onNavigate} showHomeButton={false} />

      {/* Hero Section */}
      <section className="hero-section">
        {/* Sky decorations */}
        <div className="hero-cloud hero-cloud-1">☁️</div>
        <div className="hero-cloud hero-cloud-2">☁️</div>
        <div className="hero-cloud hero-cloud-3">☁️</div>
        <div className="hero-sun">☀️</div>

        {/* Content */}
        <div className="hero-content">
          <div className="hero-banner">✨ Learning Made Fun</div>
          <h2 className="hero-title">Welcome to Your Learning Adventure! 🚀</h2>
          <p className="hero-description">
            A fun, friendly place designed just for you! Learn at your own pace with
            games, activities, and helpers who are always ready to support you.
          </p>
        </div>

        {/* Mascots */}
        <div className="hero-mascots">
          <span className="hero-mascot hero-mascot-left">🦊</span>
          <span className="hero-mascot hero-mascot-right">🦁</span>
        </div>

        {/* Rolling hills */}
        <div className="hero-hills">
          <div className="hero-hill hero-hill-back"></div>
          <div className="hero-hill hero-hill-front"></div>
        </div>
      </section>

      {/* Features Overview */}
      <section className="features-section">
        <h3 className="section-title">What Makes Us Special?</h3>
        <div className="features-grid">
          <div className="feature-card">
            <span className="feature-icon">🎮</span>
            <h4>Gamified Learning</h4>
            <p>Turn learning into an exciting game with rewards and achievements!</p>
          </div>
          <div className="feature-card">
            <span className="feature-icon">👁️</span>
            <h4>Friendly Design</h4>
            <p>Easy-to-read fonts, colors, and layouts made specially for you</p>
          </div>
          <div className="feature-card">
            <span className="feature-icon">🌟</span>
            <h4>Interactive Learning</h4>
            <p>Engage with fun and educational activities tailored for you!</p>
          </div> 
          <div className="feature-card">
            <span className="feature-icon">📚</span>
            <h4>Self-Learning</h4>
            <p>Explore and learn at your own pace with tailored resources!</p>
          </div>
        </div>
      </section>

      {/* Learning Levels */}
      <section className="levels-section">
        <h3 className="section-title">Choose Your Level 🎯</h3>
        <p className="levels-subtitle">Pick the level that matches where you are in your learning journey!</p>
        <div className="levels-grid">
          {[
            { level: 1, emoji: '🌱', label: 'Beginner', desc: 'Level 1',    color: '#B5EAD7' },
            { level: 2, emoji: '🌿', label: 'Elementary', desc: 'Level 2',  color: '#A8D5E2' },
            { level: 3, emoji: '🌻', label: 'Intermediate', desc: 'Level 3',color: '#FFD3B6' },
            { level: 4, emoji: '⭐', label: 'Advanced', desc: 'Level 4',    color: '#C7CEEA' },
            { level: 5, emoji: '🚀', label: 'Expert', desc: 'Level 5',      color: '#F9B5AC' },
            { level: 6, emoji: '🏆', label: 'Master', desc: 'Level 6',      color: '#E2A8D5' },
          ].map(({ level, emoji, label, desc, color }) => (
            <div
              key={level}
              className="level-card"
              style={{ '--level-color': color }}
              onClick={() => handleLevelClick(level)}
              role="button"
              tabIndex={0}
              onKeyDown={e => e.key === 'Enter' && handleLevelClick(level)}
            >
              <span className="level-card-emoji">{emoji}</span>
              <h4 className="level-card-label">{label}</h4>
              <p className="level-card-desc">{desc}</p>
              <span className="level-card-arrow">→</span>
            </div>
          ))}
        </div>
      </section>

      {/* Activities Carousel */}
      <section className="activities-section">
        <h3 className="section-title">Choose Your Activity 🎨</h3>
        <div className="carousel-wrapper">
          <button className="carousel-arrow carousel-arrow-left" onClick={prevSlide} aria-label="Previous activity">
            &#8678;
          </button>

          <div className="carousel-track">
            {activities.map((activity, index) => {
              const total = activities.length;
              const offset = (index - activeIndex + total) % total;
              const pos = offset <= total / 2 ? offset : offset - total;
              const isActive = pos === 0;
              const isAdjacent = Math.abs(pos) === 1;
              const isVisible = Math.abs(pos) <= 1;
              return (
                <div
                  key={index}
                  className={`carousel-card${
                    isActive ? ' carousel-card-active' : ''
                  }${isAdjacent ? ' carousel-card-adjacent' : ''}${
                    !isVisible ? ' carousel-card-hidden' : ''
                  }`}
                  style={{
                    '--card-color': activity.color,
                    transform: isActive
                      ? 'scale(1.08) translateX(0)'
                      : isAdjacent
                        ? `scale(0.88) translateX(${pos * 380}px)`
                        : `scale(0.75) translateX(${pos * 380}px)`,
                  }}
                  onClick={() => isActive ? handleActivityClick(activity.name) : setActiveIndex(index)}
                  aria-label={activity.name}
                >
                  <div className={`card-pattern ${activity.patternClass}`} />
                  <span className="carousel-card-icon">{activity.icon}</span>
                  <h4 className="carousel-card-name">{activity.name}</h4>
                  {isActive && (
                    <button className="activity-btn" onClick={e => { e.stopPropagation(); handleActivityClick(activity.name); }}>
                      Start Now →
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          <button className="carousel-arrow carousel-arrow-right" onClick={nextSlide} aria-label="Next activity">
            &#8680;
          </button>
        </div>

        {/* Caption below carousel — key forces re-mount so caption-in reruns */}
        <div key={activeIndex} className="carousel-caption">
          <h4 className="carousel-caption-title">{activities[activeIndex].name}</h4>
          <p className="carousel-caption-desc">{activities[activeIndex].description}</p>
        </div>

        {/* Dot indicators */}
        <div className="carousel-dots">
          {activities.map((_, i) => (
            <button
              key={i}
              className={`carousel-dot${i === activeIndex ? ' carousel-dot-active' : ''}`}
              onClick={() => setActiveIndex(i)}
              aria-label={`Go to activity ${i + 1}`}
            />
          ))}
        </div>
      </section>

      {/* 24/7 LexGoKids agent Banner */}
      <section
        className="tutor-banner-section"
        style={{
          background: 'linear-gradient(135deg, #FFD3B6 0%, #FFECD2 100%)',
          borderRadius: 24,
          margin: '32px 24px',
          padding: '36px 32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 24,
          boxShadow: '0 6px 24px rgba(255,160,80,0.18)',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ fontSize: '2.4rem', marginBottom: 8 }}>🤖✨</div>
          <h3 style={{ fontSize: '1.6rem', fontWeight: 800, color: '#b85c00', margin: '0 0 8px' }}>
            24/7 LexGoKids agent
          </h3>
          <p style={{ color: '#7a4000', fontSize: '1.05rem', margin: 0 }}>
            Stuck on something? Your personal LexGoKids agent is always here — ask anything, anytime!
          </p>
        </div>
        <button
          className="start-btn"
          style={{ flexShrink: 0, fontSize: '1.1rem', padding: '14px 32px', background: '#e07020', border: 'none', borderRadius: 14, color: '#fff', fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 12px rgba(224,112,32,0.3)' }}
          onClick={() => handleActivityClick('24/7 Tutoring')}
        >
          Chat Now →
        </button>
      </section>

      {/* Why Choose Us Section */}
      <section className="why-section">
        <h3 className="section-title">Why Students Love Us ❤️</h3>
        <div className="benefits-list">
          <div className="benefit-item">
            <span className="check-icon">✓</span>
            <p>Learn without stress or pressure</p>
          </div>
          <div className="benefit-item">
            <span className="check-icon">✓</span>
            <p>Get instant help whenever you're stuck</p>
          </div>
          <div className="benefit-item">
            <span className="check-icon">✓</span>
            <p>Earn badges and rewards as you progress</p>
          </div>
          <div className="benefit-item">
            <span className="check-icon">✓</span>
            <p>Practice as many times as you need</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
}

export default Home;
